<template>
  <div class="v-modal">
    <div class="v-modal__header">
      <span v-if="state == 'startOrOpen'">{{ assessmentType[0].toUpperCase() + assessmentType.slice(1) }} Assessment</span>
      <span v-else>Supply Chain</span>
      <div @click="cancel()" class="close"></div>
    </div>
    <div class="v-modal__body">
      <Errors @dismiss="errors = []" :errors="errors"/>
      <template v-if="state == 'startOrOpen'">
        <p>
          You can start a new {{ assessmentType }} assessment, or open a {{ assessmentType }} assessment that you started earlier.
          <span v-if="assessmentType == 'business'">Start a new assessment if you want to import your business locations by uploading a CSV file.</span>
        </p>
        <div v-if="userScenarios?.length > 1" class="form-check">
          <input type="radio" v-model="startOrOpen" value="openOneOf" id="action-open-one-of" class="form-check-input">
          <label for="action-open-one-of" class="form-check-label">Open one of the {{ assessmentType }} assessments from my account</label>
        </div>
        <div v-if="userScenarios?.length == 1" class="form-check">
          <input type="radio" v-model="startOrOpen" @click="scenarioCode = userScenarios[0].code" value="openOneOf" id="action-open-the" class="form-check-input">
          <label for="action-open-the" class="form-check-label">Open the {{ assessmentType }} assessment from my account: <b>{{ userScenarios[0].name }}</b></label>
        </div>
        <div class="form-check">
          <input type="radio" v-model="startOrOpen" @click="scenarioCode = null" value="open" id="action-open" class="form-check-input">
          <label for="action-open" class="form-check-label">Open an<span v-if="userScenarios?.length">other</span> existing {{ assessmentType }} assessment<span v-if="userScenarios"> and add it to my account</span></label>
        </div>
        <div class="form-check mb-3">
          <input type="radio" v-model="startOrOpen" @click="scenarioCode = null" value="start" id="action-start" class="form-check-input">
          <label for="action-start" class="form-check-label">Start a new {{ assessmentType }} assessment</label>
        </div>
      </template>
      <template v-else>
        <p>
          Your business <b>{{ scenarioName }}</b> does not have a supply chain yet. You can either create a supply chain within the current business assessment, or create one or more supply chains in separate commodity assessments. 
        </p>
        <div class="form-check">
          <input type="radio" v-model="startOrOpen" value="upgrade" id="action-upgrade" class="form-check-input">
          <label for="action-upgrade" class="form-check-label">Add a supply chain to the current business assessment</label>
        </div>
        <div class="form-check mb-3">
          <input type="radio" v-model="startOrOpen" value="start" id="action-start" class="form-check-input">
          <label for="action-start" class="form-check-label">Start a separate commodity assessment</label>
        </div>
      </template>
      <template v-if="startOrOpen == 'openOneOf'">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Assessment</th>
              <th>[ Code ]</th>
              <th style="width: 100%"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="scenario of userScenarios">
              <td>{{ scenario.name }}</td>
              <td style="padding-right: 0; white-space: nowrap"><small class="text-muted">[ {{ scenario.code }} ]</small></td>
              <td style="padding-left: 0"><button @click="copyToClipboard(scenario.code)" title="Copy this code to the clipboard" class="btn btn-link btn-sm"><i class="fa fa-copy"></i></button></td>
              <td><button @click="removeScenario(scenario)" class="btn btn-outline-danger btn-sm">Remove</button></td>
              <td v-if="userScenarios.length == 1"><button @click="selectScenario(scenario)" class="btn btn-primary btn-sm">Next</button></td>
              <td v-else style="white-space: nowrap"><button @click="selectScenario(scenario)" class="btn btn-primary btn-sm">Select & Next</button></td>
            </tr>
          </tbody>
        </table>
      </template>
      <template v-else-if="startOrOpen == 'open'">
        <p>
          Enter the code of your {{ assessmentType }} assessment.
        </p>
        <form @submit.prevent="next">
          <input type="text" v-model="scenarioCode" placeholder="Code" class="form-control">
          <div class="form-text">The code that you saved when you started your {{ assessmentType }} assessment</div>
        </form>
      </template>
      <template v-else-if="startOrOpen == 'start'">
        <p>
          Give your {{ assessmentType }} assessment any name you like, such as the name of your {{ assessmentType }}.
        </p>
        <div class="row mb-3">
          <label for="scenario-name" class="col-2 col-form-label">Name</label>
          <div class="col-auto">
            <input type="text" v-model="tempScenario.name" id="scenario-name" :placeholder="'My ' + assessmentType[0].toUpperCase() + assessmentType.slice(1)" class="form-control">
          </div>
        </div>
        <div class="form-check mb-3">
          <input type="checkbox" v-model="scenarioQuantityEnabled" id="scenario-quantity-enabled" class="form-check-input">
          <label for="scenario-quantity-enabled" class="form-check-label">Assess a specific quantity</label>
        </div>
        <template v-if="scenarioQuantityEnabled">
          <p>
            Enter the output quantity of your {{ assessmentType }} if you want to calculate the water footprint per output unit. The quantity can be either physical or monetary.
            <span v-if="assessmentType == 'business'">It is the combined output of all the locations of your business.</span>
          </p>
          <div class="row">
            <label for="scenario-quantity" class="col-2 col-form-label">Quantity</label>
            <div class="col-auto">
              <input type="number" v-model.number="tempScenario.quantity" id="scenario-quantity" placeholder="0" class="form-control">
            </div>
            <div class="col-auto">
              <select v-model="tempScenario.unitID" class="form-select">
                <option v-for="unit in quantityUnits" :value="unit.unitId" :key="unit.unitId">
                  {{ unit.long }}
                </option>
              </select>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="v-modal__footer">
      <button v-if="startOrOpen != 'openOneOf'" @click="next()" class="btn btn-primary">Next</button>
    </div>
  </div>
</template>

<script>
  import { assessment } from "@/services/assessment-model.js";
  import { quantityUnits, scenariosLoader } from "@/services/entity-model.js";
  import { closeModal, modalTypes, openModal } from "@/services/modal-model";
  import { userModel } from "@/services/user-model";
  import Errors from "@/components/shared/Errors.vue";

  export default {
    name: "Scenario",
    components: {
      Errors
    },
    props: {
      modal: {type: Object}
    },
    data() {
      return {
        quantityUnits,
        errors: [],
        startOrOpen: null,
        scenarioQuantityEnabled: false, 
        scenarioCode: null,
        tempScenario: {
          name: null,
          type: null,
          unitID: 1,
          quantity: 0
        }
      };
    },
    computed: {
      state() {
        if ((assessment.assessmentType.name == "business") && (assessment.step.name != "scope")) {
          return "upgradeOrStart";
        }
        return "startOrOpen";
      },
      assessmentType() {
        if ((assessment.assessmentType.name == "business") && (assessment.step.name != "scope")) {
          return "commodity";
        }
        return assessment.assessmentType.name;
      },
      userScenarios() {
        if (!userModel.user) {
          return null;
        }
        return userModel.user.scenarios.filter(
          (assessment.assessmentType.name == "commodity") ? scenario => scenario.type % 10 == 1 : scenario => scenario.type % 100 >= 10
        );
      },
      scenarioName() {
        return assessment.scenario.name;
      }
    },
    watch: {
      startOrOpen() {
        this.errors = [];
      },
      userScenarios(val) {
        if (val.length == 0) {
          this.startOrOpen = null;
        }
      }
    },
    methods: {
      cancel() {
        this.close();
        if (this.state == "startOrOpen") {
          assessment.setAssessmentType(null);
        }
      },
      copyToClipboard(text) {
        navigator.clipboard.writeText(text);
      },
      removeScenario(scenario) {
        userModel.user.deleteScenario(scenario.code);
      },
      selectScenario(scenario) {
        this.scenarioCode = scenario.code;
        this.next();
      },
      next() {
        if (this.startOrOpen == "start") {
          this.setTempScenario();
          this.validateStart();
          if (this.errors.length == 0) {
            const modalType = (this.assessmentType == "commodity") ? modalTypes.scenarioCountryCrop : modalTypes.process;
            if (this.state == "upgradeOrStart") {
              assessment.setAssessmentType("commodity");
            }
            this.close();
            openModal(modalType, { tempScenario: this.tempScenario, width: 700/*, height: "auto", draggable: true, scrollable: true*/ });
          }
        } else if ((this.startOrOpen == "openOneOf") || (this.startOrOpen == "open")) {
          this.validateOpen();
          if (this.errors.length == 0) {
            scenariosLoader.load(this.scenarioCode, scenarios => {
              if ((this.startOrOpen == "openOneOf") || (this.startOrOpen == "open")) {
                this.validateOpen(scenarios);
                if (this.errors.length == 0) {
                  if ((this.userScenarios != null) && !this.userScenarios.some(scenario => scenario.code == scenarios[0].code)) {
                    userModel.user.saveScenario(scenarios[0].code);
                  }
                  this.close();
                  assessment.setScenario(scenarios[0]);
                  assessment.nextStep();
                }
              }
            });
          }
        } else if (this.startOrOpen == "upgrade") {
          this.close();
          openModal(modalTypes.scenarioCountryCrop, { width: 700/*, height: "auto", draggable: false, scrollable: true*/ });
        } else {
          this.errors = ["Make a choice."];
        }
      },
      setTempScenario() {
        this.tempScenario.type = (this.assessmentType == "commodity") ? 101 : 10;
        if (!this.scenarioQuantityEnabled) {
          this.tempScenario.unitID = 1;
          this.tempScenario.quantity = 0;
        }
      },
      validateStart() {
        this.errors = [];
        if ((this.tempScenario.name == null) || (this.tempScenario.name == "")) {
          this.errors.push("A name is required for your " + this.assessmentType + " assessment.");
        }
        if (this.scenarioQuantityEnabled && (this.tempScenario.quantity <= 0)) {
          this.errors.push("Quantity must be greater than 0.");
        }
      },
      validateOpen(scenarios = null) {
        this.errors = [];
        if (scenarios == null) {
          if (this.scenarioCode == null) {
            this.errors.push("Enter an assessment code.");
          } else if (this.scenarioCode.length != 10) {
            this.errors.push("An assessment code is 10 characters long.");
          }
        } else if (scenarios.length == 0) {
          this.errors.push("Assessment code not found.");
        } else if (this.assessmentType == "commodity") {
          scenarios = scenarios.filter(scenario => scenario.type % 10 == 1);
          if (scenarios.length == 0) {
            this.errors.push("That code does not represent a commodity assessment.");
          }
        } else {  // this.assessmentType == "business"
          scenarios = scenarios.filter(scenario => scenario.type % 100 >= 10);
          if (scenarios.length == 0) {
            this.errors.push("That code does not represent a business assessment with locations.");
          }
        }
      },
      close() {
        closeModal(this.modal);
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>
